"use client";

import { useState } from "react";
import Link from "next/link";
import { HiHashtag, HiClipboardCopy, HiCheck, HiQuestionMarkCircle } from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import UnifiedFAQ from "@/components/UnifiedFAQ";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

type Niche =
  | "fitness"
  | "travel"
  | "food"
  | "fashion"
  | "tech"
  | "business"
  | "beauty"
  | "photography"
  | "music"
  | "art";

interface HashtagSet {
  popular: string[];
  medium: string[];
  niche: string[];
}

const HASHTAG_DATA: Record<Niche, HashtagSet> = {
  fitness: {
    popular: [
      "#fitness",
      "#gym",
      "#workout",
      "#fitnessmotivation",
      "#health",
      "#bodybuilding",
      "#training",
      "#fit",
      "#lifestyle",
      "#motivation",
    ],
    medium: [
      "#fitfam",
      "#strengthtraining",
      "#homeworkout",
      "#cardio",
      "#musclebuilding",
      "#gymlife",
      "#fitnessgirl",
      "#workoutmotivation",
      "#fitnessjourney",
      "#personaltrainer",
    ],
    niche: [
      "#fitnesscoach",
      "#strengthcoach",
      "#musclegrowth",
      "#weightroom",
      "#preworkout",
      "#gymrat",
      "#fitcheck",
      "#ironparadise",
      "#liftinglife",
      "#sweatlife",
    ],
  },
  travel: {
    popular: [
      "#travel",
      "#travelgram",
      "#wanderlust",
      "#travelphotography",
      "#explore",
      "#vacation",
      "#adventure",
      "#instatravel",
      "#trip",
      "#travelling",
    ],
    medium: [
      "#travellife",
      "#travelblogger",
      "#backpacker",
      "#solotravel",
      "#travelcouple",
      "#roadtrip",
      "#globetrotter",
      "#traveldiaries",
      "#bucketlist",
      "#travelinspiration",
    ],
    niche: [
      "#hiddengemtravel",
      "#offthebeatenpath",
      "#slowtravel",
      "#budgettravel",
      "#digitalnomadd",
      "#worldtraveler",
      "#countrycollector",
      "#traveltips",
      "#sustainabletravel",
      "#traveldreams",
    ],
  },
  food: {
    popular: [
      "#food",
      "#foodie",
      "#foodphotography",
      "#instafood",
      "#foodblogger",
      "#delicious",
      "#yummy",
      "#homemade",
      "#cooking",
      "#recipe",
    ],
    medium: [
      "#foodlover",
      "#healthyfood",
      "#foodstagram",
      "#foodpics",
      "#homecooking",
      "#mealprep",
      "#vegetarian",
      "#vegan",
      "#foodart",
      "#tasty",
    ],
    niche: [
      "#plantbased",
      "#glutenfree",
      "#mealprepping",
      "#cleaneating",
      "#wholefoods",
      "#eattherainbow",
      "#sustainableeating",
      "#fermentation",
      "#fromthegarden",
      "#farmtotable",
    ],
  },
  fashion: {
    popular: [
      "#fashion",
      "#style",
      "#ootd",
      "#outfit",
      "#fashionblogger",
      "#streetstyle",
      "#outfitoftheday",
      "#fashionista",
      "#stylish",
      "#clothes",
    ],
    medium: [
      "#fashioninspo",
      "#styleinspo",
      "#outfitinspiration",
      "#casualstyle",
      "#streetfashion",
      "#fashionstyle",
      "#womensfashion",
      "#mensfashion",
      "#outfitcheck",
      "#stylegoals",
    ],
    niche: [
      "#slowfashion",
      "#sustainablestyle",
      "#vintagefashion",
      "#thrifted",
      "#capsulewardrobe",
      "#consciousfashion",
      "#secondhand",
      "#minimalstyle",
      "#ethicalfashion",
      "#rewear",
    ],
  },
  tech: {
    popular: [
      "#tech",
      "#technology",
      "#coding",
      "#programming",
      "#developer",
      "#software",
      "#startup",
      "#innovation",
      "#ai",
      "#gadgets",
    ],
    medium: [
      "#techblogger",
      "#webdev",
      "#javascript",
      "#python",
      "#openai",
      "#machinelearning",
      "#cybersecurity",
      "#cloudcomputing",
      "#devops",
      "#uxdesign",
    ],
    niche: [
      "#buildinpublic",
      "#indiehacker",
      "#solofounder",
      "#saas",
      "#productlaunch",
      "#techfounder",
      "#codelife",
      "#devlife",
      "#100daysofcode",
      "#buildinginpublic",
    ],
  },
  business: {
    popular: [
      "#business",
      "#entrepreneur",
      "#success",
      "#motivation",
      "#marketing",
      "#startup",
      "#leadership",
      "#hustle",
      "#money",
      "#mindset",
    ],
    medium: [
      "#entrepreneurship",
      "#businessowner",
      "#smallbusiness",
      "#digitalmarketing",
      "#growthmindset",
      "#contentmarketing",
      "#businesstips",
      "#sidehustle",
      "#workfromhome",
      "#onlinebusiness",
    ],
    niche: [
      "#bootstrapped",
      "#solopreneur",
      "#b2bmarketing",
      "#growthstrategy",
      "#founderlife",
      "#productivityhacks",
      "#revenuegrowth",
      "#b2bsales",
      "#startuplife",
      "#scaleyourbusiness",
    ],
  },
  beauty: {
    popular: [
      "#beauty",
      "#makeup",
      "#skincare",
      "#glam",
      "#makeuptutorial",
      "#beautyblogger",
      "#cosmetics",
      "#lipstick",
      "#eyeshadow",
      "#beautyproducts",
    ],
    medium: [
      "#makeuplook",
      "#skincareroutine",
      "#naturalmakeup",
      "#glowup",
      "#beautytips",
      "#makeupoftheday",
      "#skincareproducts",
      "#makeupjunkie",
      "#cleanbeauty",
      "#beautylovers",
    ],
    niche: [
      "#kbeauty",
      "#skintok",
      "#glasskin",
      "#slugging",
      "#acidtoner",
      "#retinol",
      "#doublecleansing",
      "#occlusivemoisturizer",
      "#niacinamide",
      "#hyaluronicacid",
    ],
  },
  photography: {
    popular: [
      "#photography",
      "#photo",
      "#photographer",
      "#portrait",
      "#nature",
      "#landscape",
      "#photooftheday",
      "#picoftheday",
      "#canon",
      "#nikon",
    ],
    medium: [
      "#portraitphotography",
      "#landscapephotography",
      "#streetphotography",
      "#naturephotography",
      "#wildlifephotography",
      "#travelphotography",
      "#architecturephotography",
      "#photoshoot",
      "#lightroom",
      "#photoedit",
    ],
    niche: [
      "#filmphotography",
      "#analogphotography",
      "#35mm",
      "#filmisnotdead",
      "#shootfilm",
      "#leica",
      "#mediumformat",
      "#darkroom",
      "#tintype",
      "#cameraclub",
    ],
  },
  music: {
    popular: [
      "#music",
      "#musician",
      "#song",
      "#guitar",
      "#piano",
      "#producer",
      "#newmusic",
      "#singer",
      "#hiphop",
      "#pop",
    ],
    medium: [
      "#musicproducer",
      "#songwriter",
      "#musicvideo",
      "#indiemusic",
      "#musician",
      "#recording",
      "#studiolife",
      "#beatmaker",
      "#livemusic",
      "#musicislife",
    ],
    niche: [
      "#unsignedartist",
      "#indieartist",
      "#beatmaking",
      "#homestudio",
      "#musicproduction",
      "#mixingengineer",
      "#masteringengineer",
      "#audiophile",
      "#vinylcollector",
      "#lofimusic",
    ],
  },
  art: {
    popular: [
      "#art",
      "#artist",
      "#artwork",
      "#drawing",
      "#painting",
      "#illustration",
      "#creative",
      "#design",
      "#digitalart",
      "#sketch",
    ],
    medium: [
      "#artofinstagram",
      "#artistsoninstagram",
      "#artdaily",
      "#contemporaryart",
      "#modernart",
      "#abstractart",
      "#watercolor",
      "#oilpainting",
      "#conceptart",
      "#characterdesign",
    ],
    niche: [
      "#procreate",
      "#digitalillustration",
      "#gouache",
      "#inktober",
      "#sketchbook",
      "#artcommissions",
      "#artforsale",
      "#originalart",
      "#emergingartist",
      "#artcollector",
    ],
  },
};

const NICHE_LABELS: Record<Niche, string> = {
  fitness: "Fitness & Gym",
  travel: "Travel",
  food: "Food & Cooking",
  fashion: "Fashion & Style",
  tech: "Tech & Coding",
  business: "Business & Entrepreneurship",
  beauty: "Beauty & Skincare",
  photography: "Photography",
  music: "Music",
  art: "Art & Illustration",
};

const FAQ_ITEMS = [
  {
    question: "What are the three hashtag tiers?",
    answer:
      "Popular hashtags have hundreds of millions of posts and offer broad exposure but high competition. Medium hashtags have millions of posts and a better chance of being discovered. Niche hashtags have thousands to low millions of posts and reach a highly targeted, engaged audience. A good strategy uses all three tiers.",
  },
  {
    question: "How many hashtags should I use per Instagram post?",
    answer:
      "Up to 5. Instagram's Help Center now says you can use up to 5 hashtags on a post, and that a comment with more than 5 won't post. The old 30-hashtag advice is out of date. This tool gives you a ready 5-tag mix (one popular, two medium, two niche) drawn from 30 across the three tiers, so each post can use a different five.",
  },
  {
    question: "Should I put hashtags in the caption or comments?",
    answer:
      "Instagram supports both: you can add hashtags in the caption or in a comment on your post, and either way a public post appears on the hashtag's page. The first comment keeps your caption cleaner; the caption keeps everything in one place. The 5-tag limit applies either way.",
  },
  {
    question: "How often should I change my hashtags?",
    answer:
      "Vary them to match each post. Five tags chosen for what the post is actually about are more useful than the same five pasted every time, and the tiers below give you 30 to choose from.",
  },
  {
    question: "Are these hashtags updated in real-time?",
    answer:
      "The hashtags in this tool are curated by niche and represent a strong, evergreen selection. They are not pulled live from Instagram's API. For the very latest trending hashtags in your niche, cross-reference with Instagram's own search suggestions.",
  },
];

export default function InstagramHashtagGeneratorClient() {
  const [selectedNiche, setSelectedNiche] = useState<Niche | null>(null);
  const [copied, setCopied] = useState<"all" | "popular" | "medium" | "niche" | null>(null);

  const hashtags = selectedNiche ? HASHTAG_DATA[selectedNiche] : null;

  // Instagram caps a post at 5 hashtags (help.instagram.com/351460621611097), so the
  // primary copy is a 5-tag mix — 1 popular, 2 medium, 2 niche — not all 30.
  const fiveTagMix = hashtags
    ? [hashtags.popular[0], ...hashtags.medium.slice(0, 2), ...hashtags.niche.slice(0, 2)].join(" ")
    : "";

  const copyText = async (text: string, key: "all" | "popular" | "medium" | "niche") => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="Instagram Hashtag Generator"
        description="Get a ready 5-tag Instagram hashtag mix for your niche, plus the full 30 across popular, medium, and niche tiers to swap from."
        url="https://plugmonkey.xyz/tool/instagram-hashtag-generator/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "Instagram Hashtag Generator",
            url: "https://plugmonkey.xyz/tool/instagram-hashtag-generator/",
          },
        ]}
        faq={FAQ_ITEMS}
      />

      {/* Hero */}
      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-pink-500/10 rounded-full text-pink-500">
          <HiHashtag className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">Instagram Hashtag Generator</h1>
          <p className="text-muted-foreground mt-1">
            A ready 5-tag mix for your niche, drawn from 30 across three tiers.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          Instagram now accepts up to 5 hashtags on a post, so the five you choose matter. Popular
          hashtags reach large audiences, medium hashtags give you a realistic chance of being seen,
          and niche hashtags reach a smaller, more targeted community.
        </p>
        <p>
          Select your niche below to get a 5-tag mix ready to paste into your caption, plus 10
          hashtags in each tier to swap in for the ones that fit your post less well.
        </p>
      </div>

      {/* Niche selector */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-8">
        {(Object.keys(NICHE_LABELS) as Niche[]).map((niche) => (
          <button
            key={niche}
            onClick={() => {
              setSelectedNiche(niche);
              setCopied(null);
            }}
            className={`rounded-xl border px-3 py-2 text-sm font-medium transition-all text-center ${
              selectedNiche === niche
                ? "bg-pink-500 text-white border-pink-500 shadow-md"
                : "bg-card border-border hover:border-pink-400 hover:text-pink-500"
            }`}
            aria-pressed={selectedNiche === niche}
          >
            {NICHE_LABELS[niche]}
          </button>
        ))}
      </div>

      {/* Results */}
      {hashtags && (
        <div className="space-y-6">
          {/* Copy All */}
          <div className="flex items-center justify-between bg-pink-500/5 border border-pink-500/20 rounded-2xl p-4">
            <span className="text-sm font-medium">
              5-tag mix: <span className="font-mono text-muted-foreground">{fiveTagMix}</span>
            </span>
            <Button
              size="sm"
              onClick={() => copyText(fiveTagMix, "all")}
              className="bg-pink-500 hover:bg-pink-600 text-white gap-1"
            >
              {copied === "all" ? (
                <>
                  <HiCheck className="h-4 w-4" /> Copied!
                </>
              ) : (
                <>
                  <HiClipboardCopy className="h-4 w-4" /> Copy 5
                </>
              )}
            </Button>
          </div>

          {/* Tier cards */}
          {(
            [
              {
                key: "popular",
                label: "Popular",
                color: "text-red-500",
                bg: "bg-red-500/10",
                description: "High volume, broad exposure",
              },
              {
                key: "medium",
                label: "Medium",
                color: "text-yellow-500",
                bg: "bg-yellow-500/10",
                description: "Competitive but achievable ranking",
              },
              {
                key: "niche",
                label: "Niche",
                color: "text-green-600",
                bg: "bg-green-500/10",
                description: "Targeted, engaged community",
              },
            ] as const
          ).map(({ key, label, color, bg, description }) => (
            <Card key={key}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className={`flex items-center gap-2 ${color}`}>
                      <span
                        className={`inline-block w-2 h-2 rounded-full ${bg.replace("/10", "")}`}
                      />
                      {label} Hashtags
                    </CardTitle>
                    <CardDescription>{description}</CardDescription>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyText(hashtags[key].join(" "), key)}
                    className="gap-1"
                  >
                    {copied === key ? (
                      <>
                        <HiCheck className="h-4 w-4 text-green-500" /> Copied
                      </>
                    ) : (
                      <>
                        <HiClipboardCopy className="h-4 w-4" /> Copy
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {hashtags[key].map((tag) => (
                    <span
                      key={tag}
                      className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${bg} ${color}`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}

          <div className="bg-card border rounded-xl p-6">
            <h3 className="font-bold text-lg mb-2">Managing Instagram followers?</h3>
            <p className="text-muted-foreground text-sm mb-4">
              <strong>Followers Exporter Pro for Instagram</strong> lets you export your follower
              and following lists to CSV for audience analysis and outreach.
            </p>
            <Button asChild variant="secondary">
              <Link href="/product/instagram-followers-exporter-pro/">View Extension</Link>
            </Button>
          </div>
        </div>
      )}

      {/* FAQ */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ items={FAQ_ITEMS} showSupportLink={false} className="space-y-4" />
          </div>
        </div>
      </section>

      <ToolsCustomDevCTA />
    </div>
  );
}
